import {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  matchPath,
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import {OfficeContext} from '../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../contexts/office/types';
import {SnackBarContext} from '../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../contexts/snack-bar/types';
import {translations} from '../../../../../../i18n/translations';
import {PlaylistsRepository} from '../../../../../../repositories/playlists';
import {AppLanguageTag} from '../../../../../../i18n/types';
import {SectionTabs} from '../../../../../../components/section-tabs/section-tabs';
import {Box} from '@mui/material';
import {ContentPage} from '../../../../../../components/content-page/content-page';
import PlaylistItemDetailsSystemAdministratorOfficeScreen from './details/playlist-item-details.system-administrator.screen';
import PlaylistItemEntitiesSystemAdministratorOfficeScreen from './entities/playlist-item-entities.system-administrator.screen';
import i18n from 'i18next';

enum PlaylistItemSectionTabs {
  Details = 'details',
  Entities = 'entities',
}

enum PathExtensions {
  Entities = 'entities',
}

export default function PlaylistItemSystemAdministratorOfficeScreen() {
  // Get params
  // - id: playlist id
  // - itemId: playlist item id
  let {id, itemId}: {id?: string; itemId?: string} = useParams();
  if (itemId?.toLowerCase() === 'create') {
    itemId = undefined;
  }

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {path, url} = useRouteMatch();

  // Get navigation info
  let history = useHistory();

  // Translations
  const {t} = useTranslation();

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Get current tab based on the location
  const handleGetCurrentTab = (pathname: string) => {
    if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.Entities}`,
        exact: true,
        strict: false,
      })
    ) {
      return PlaylistItemSectionTabs.Entities;
    }

    // Default
    return PlaylistItemSectionTabs.Details;
  };

  // Hold state
  const [currentTab, setCurrentTab] = useState<PlaylistItemSectionTabs | undefined>(
    handleGetCurrentTab(window.location.pathname)
  );

  // Listen for route changes
  useEffect(() => {
    // Listen for route changes
    const routeChangesUnsubscribe = history.listen((location: any) => {
      // Let's update the current tab
      setCurrentTab(handleGetCurrentTab(location.pathname));
    });

    // Clean up
    return () => {
      // Unsubscribe
      routeChangesUnsubscribe();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, path]);

  // Validate and get the playlist item
  useEffect(() => {
    const getPlaylistItem = async () => {
      if (itemId) {
        try {
          // Check id
          if (!id) {
            throw new Error('Empty playlist id.');
          }

          // Get playlist item
          // Note: Don't localize because we need the original model for the playlist item form
          const result =
            await PlaylistsRepository.instance.getPlaylistItemByIdAsSystemAdministrator(
              id,
              itemId,
              false
            );

          if (result) {
            // Set playlist item in context
            officeContext.setPlaylistItemForm(result);
          } else {
            // Error
            throw new Error('Empty playlist item.');
          }
        } catch (error) {
          // Log
          console.debug('🔴 Could not get playlist item.', error);

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(
              translations.playlists.system_administrator.playlist_item.errors
                .item_does_not_exist_error
            ),
          });

          // Redirect
          history.push(`${url.replace(`/items/${itemId}`, '/items')}`);
        }
      } else {
        // Set playlist item for create
        officeContext.setPlaylistItemForm({
          isActive: true,
          title: '',
          resourceUrl: '',
          playlistId: id || '',
        });
      }
    };

    // Get playlist item
    getPlaylistItem();

    // Clean up
    return () => {
      // Clear playlist item
      officeContext.clearPlaylistItemForm();

      // Log
      console.debug('🧹 Playlist item form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  // Get title
  let title: string = '';
  switch (i18n.language) {
    case AppLanguageTag.ptPT:
      title =
        officeContext.state.playlistItemForm?.titleLocalized?.ptPT ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    case AppLanguageTag.esES:
      title =
        officeContext.state.playlistItemForm?.titleLocalized?.esES ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    case AppLanguageTag.glES:
      title =
        officeContext.state.playlistItemForm?.titleLocalized?.glES ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    case AppLanguageTag.enUS:
      title =
        officeContext.state.playlistItemForm?.titleLocalized?.enUS ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    case AppLanguageTag.ptBR:
      title =
        officeContext.state.playlistItemForm?.titleLocalized?.ptBR ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    default:
      title = officeContext.state.playlistItemForm?.title || '';
      break;
  }

  return (
    <ContentPage>
      {itemId && (
        <Box sx={{marginBottom: '16px'}}>
          <SectionTabs
            title={title}
            titleAvatar={{
              url: officeContext.state.playlistItemForm?.thumbnailUrl,
              type: 'other',
            }}
            currentTab={currentTab}
            tabs={[
              {
                value: PlaylistItemSectionTabs.Details,
                label: t(translations.playlists.system_administrator.playlist_item.tabs.details),
                href: url,
              },
              {
                value: PlaylistItemSectionTabs.Entities,
                label: t(translations.playlists.system_administrator.playlist_item.tabs.entities),
                href:
                  url.charAt(url.length - 1) === '/'
                    ? `${url}${PathExtensions.Entities}`
                    : `${url}/${PathExtensions.Entities}`,
              },
            ]}
            exitUrl={`${url.replace(`/items/${itemId}`, '/items')}`}
          />
        </Box>
      )}
      {/* Main content */}
      <Box>
        <Switch>
          {/* Playlist item details */}
          <Route exact path={path}>
            <PlaylistItemDetailsSystemAdministratorOfficeScreen />
          </Route>
          {/* Playlist item entities */}
          <Route exact path={`${path}/${PathExtensions.Entities}`}>
            <PlaylistItemEntitiesSystemAdministratorOfficeScreen />
          </Route>

          {/** Default route */}
          <Route path={path}>
            <Redirect to={url} />
          </Route>
        </Switch>
      </Box>
    </ContentPage>
  );
}
